import * as React from "react";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { EyeIcon, Ban, MessageSquareIcon, Trash2Icon } from "lucide-react";
import { Link, useSearchParams } from "react-router";
import { Button } from "@/components/ui/button";
import { useToggle } from "react-use";
import { BusinessReportOutcomeDeleteModal } from "@/modules/snapshots/modals/businessReportOutComeDeleteModal";
import { useCancelBusinessReportOutcomeMutation } from "@/modules/snapshots/businessReports.api";
import { toast } from "react-toastify";
import { getCollectionLockMessage } from "@/views/contract-families/pipeline/pipelineErrors";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
}

export const SnapshotCollectionsOutcomesRowActions: React.FC<Props> = ({ businessReportOutcome }) => {
  const [showRemoveModal, setShowRemoveModal] = useToggle(false);
  const [cancelOutcome] = useCancelBusinessReportOutcomeMutation();
  const [searchParams] = useSearchParams();
  const subcollectionId = searchParams.get("subcollection_id") ?? undefined;

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await cancelOutcome({ id: businessReportOutcome.id }).unwrap();
    } catch (error) {
      toast.error(getCollectionLockMessage(error, "The SnapShot could not be cancelled."));
    }
  };

  if (!businessReportOutcome.fs_entry) {
    return null;
  }
  return (
    <>
      <BusinessReportOutcomeDeleteModal
        businessReportOutcome={businessReportOutcome}
        isOpen={showRemoveModal}
        setIsOpen={setShowRemoveModal}
        subcollectionId={subcollectionId}
      />
      <div className="flex justify-end pr-2">
        {(businessReportOutcome.status === "IN_PROGRESS" || businessReportOutcome.status === "NEW") && (
          <Button
            variant="second-white"
            size="sm"
            className="mr-1 flex items-center gap-2 px-3 py-2 text-xs"
            type="button"
            onClick={event => void handleCancel(event)}
          >
            <Ban className="h-4 w-4 flex-shrink-0" /> Cancel
          </Button>
        )}
        <Link to={`/reports/outcome/${businessReportOutcome.id}`} title="View SnapShot">
          <Button
            disabled={businessReportOutcome.status === "NEW"}
            variant="second-white"
            size="sm"
            className="mr-1 flex items-center gap-2 px-3 py-2 text-xs"
            type="button"
          >
            <EyeIcon className="h-4 w-4 flex-shrink-0" /> View
          </Button>
        </Link>

        <Button variant="second-white" size="sm" className="mr-1 w-full text-xs sm:w-auto" asChild>
          <Link to={`/chat/${businessReportOutcome.fs_entry.id}`} state={{ fromSnapshot: "/snapshots" }}>
            <MessageSquareIcon className="mr-2 h-4 w-4" />
            Q&A
          </Link>
        </Button>

        <Button
          variant="second-white"
          size="sm"
          className="flex items-center gap-2 px-3 py-2 text-xs"
          type="button"
          onClick={setShowRemoveModal}
        >
          <Trash2Icon className="h-4 w-4 flex-shrink-0" />
        </Button>
      </div>
    </>
  );
};
